import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useUser } from '../hooks/useApi';
import { useFormatDate } from '../hooks/useFormatDate';
import type { ActivityItem } from '../types';

type Kind = ActivityItem['kind'];
type Filter = 'all' | Kind;

type GroupBy = 'time' | 'workflow';

/* ---------- per-kind visuals ---------- */
const KIND_STYLE: Record<Kind, { dot: string; chip: string }> = {
  engagement: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700' },
  email: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700' },
  portal: { dot: 'bg-primary-500', chip: 'bg-primary-50 text-primary-700' },
};

/* ---------- workflow grouping ----------
 * Every item (across all three kinds) maps to one intuitive bucket, derived from
 * kind + event + page + click label, so the feed can be scanned "by what it's
 * about" rather than only by time. Order below is the display order. */
type WorkflowKey = 'badges' | 'issuing' | 'engagement' | 'email' | 'team' | 'developers' | 'browsing';
const WORKFLOW_ORDER: WorkflowKey[] = ['badges', 'issuing', 'engagement', 'email', 'team', 'developers', 'browsing'];
const WORKFLOW_META: Record<WorkflowKey, { icon: string; en: string; dot: string }> = {
  badges:     { icon: '🎨', en: 'Badges & design',      dot: 'bg-primary-500' },
  issuing:    { icon: '📜', en: 'Issuing & sending',    dot: 'bg-indigo-500' },
  engagement: { icon: '👀', en: 'Recipient engagement', dot: 'bg-emerald-500' },
  email:      { icon: '✉️', en: 'Email delivery',       dot: 'bg-amber-500' },
  team:       { icon: '👥', en: 'Team & account',       dot: 'bg-sky-500' },
  developers: { icon: '🔌', en: 'Developers',           dot: 'bg-rose-500' },
  browsing:   { icon: '🧭', en: 'Browsing',             dot: 'bg-gray-400' },
};

function categorize(e: ActivityItem): WorkflowKey {
  if (e.kind === 'engagement') return 'engagement';
  if (e.kind === 'email') return 'email';
  // portal (in-app team activity): classify by event, then page, then click label.
  const ev = e.event || '';
  const page = e.page || '';
  const label = (e.label || '').toLowerCase();
  if (ev.startsWith('badge_create') || ev.startsWith('designer_')) return 'badges';
  if (page.startsWith('/badges') || page.startsWith('/my-templates')) return 'badges';
  if (label.startsWith('pick badge') || label === 'create badge' || label === 'open badge picker') return 'badges';
  if (page.startsWith('/bulk-awards') || page.startsWith('/awards') || page.startsWith('/my-awards')) return 'issuing';
  if (label === 'issue_credential' || label === 'bulk_send_all' || label.startsWith('select a badge')) return 'issuing';
  if (page.startsWith('/members') || page.startsWith('/profile') || page.startsWith('/settings') || page.startsWith('/billing')) return 'team';
  if (label === 'nav_profile' || label === 'send invite') return 'team';
  if (page.startsWith('/webhooks') || page.startsWith('/api-keys') || page.startsWith('/connectors')) return 'developers';
  if (label.includes('add endpoint') || label === 'nav_docs') return 'developers';
  return 'browsing';
}

/* ---------- per-user (person) grouping ----------
 * The person an event is attributed to: the in-app ACTOR for portal events
 * (you / a teammate), or the RECIPIENT for engagement + email (an external
 * credential recipient). Role drives the tag + the include-recipients filter. */
type Role = 'you' | 'team' | 'recipient';
const ROLE_META: Record<Role, { en: string; tag: string }> = {
  you:       { en: 'You',       tag: 'bg-primary-50 text-primary-700' },
  team:      { en: 'Team',      tag: 'bg-sky-50 text-sky-700' },
  recipient: { en: 'Recipient', tag: 'bg-gray-100 text-gray-600' },
};

function personOf(e: ActivityItem, myEmail: string): { key: string; name: string; role: Role } {
  if (e.kind === 'portal') {
    const actor = e.actor || '';
    if (!actor) return { key: '__system__', name: 'System', role: 'team' };
    const role: Role = myEmail && actor.toLowerCase() === myEmail ? 'you' : 'team';
    return { key: `a:${actor.toLowerCase()}`, name: actor, role };
  }
  // engagement / email → the external recipient.
  const r = e.recipient || '';
  if (!r) return { key: '__anon__', name: 'Anonymous viewer', role: 'recipient' };
  return { key: `r:${r.toLowerCase()}`, name: r, role: 'recipient' };
}

function initialOf(name: string): string {
  const c = (name.trim()[0] || '?').toUpperCase();
  return /[A-Z0-9]/.test(c) ? c : '?';
}

// Humanize an unmapped event name: "designer_color_change" → "color change".
function humanize(event: string): string {
  return event.replace(/^designer_/, '').replace(/_/g, ' ');
}

function relTime(ms: number, now: number): string {
  const s = Math.max(1, Math.floor((now - ms) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export function ActivityPage() {
  const { t, i18n } = useTranslation('common');
  const formatDate = useFormatDate();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [before, setBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [groupBy, setGroupBy] = useState<GroupBy>('time');
  const [groupByUser, setGroupByUser] = useState(true);
  const [includeRecipients, setIncludeRecipients] = useState(true);
  const [now] = useState(() => Date.now());
  const { data: me } = useUser();
  const myEmail = (me?.email || '').toLowerCase();

  const load = useCallback(async (cursor: number | null) => {
    const res = await api.getActivity(cursor);
    setItems(prev => (cursor ? [...prev, ...res.items] : res.items));
    setBefore(res.nextBefore);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false);
    api.getActivity(null)
      .then(res => { if (alive) { setItems(res.items); setBefore(res.nextBefore); } })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const loadMore = async () => {
    if (!before || loadingMore) return;
    setLoadingMore(true);
    try { await load(before); } catch { /* ignore */ } finally { setLoadingMore(false); }
  };

  // Describe one item as a sentence. Verbs/labels are translated; the granular
  // designer long-tail falls back to a humanized event name.
  // %-placeholders are filled here (i18next never touches %…%, so no interpolation surprises).
  const fill = (s: string, vars: Record<string, string>) =>
    Object.entries(vars).reduce((acc, [k, v]) => acc.split(`%${k}%`).join(v), s);

  const describe = (e: ActivityItem): { text: string; sub?: string | null } => {
    const vars = {
      badge: e.badge || t('activity.aCredential', 'a credential'),
      who: e.recipient || t('activity.aViewer', 'a viewer'),
      actor: e.actor || t('activity.aTeammate', 'a teammate'),
      page: e.page || t('activity.aPage', 'a page'),
      label: humanize(e.label || e.event),
      what: humanize(e.event),
    };
    const net = e.network ? ` (${t(`analytics.net.${e.network}`, e.network)})` : '';
    if (e.kind === 'engagement') {
      const v: Record<string, string> = {
        view: t('activity.eng.viewed', '%who% viewed %badge%'),
        share: t('activity.eng.shared', '%who% shared %badge%'),
        download: t('activity.eng.downloaded', '%who% downloaded %badge%'),
        click_verify: t('activity.eng.verified', '%who% verified %badge%'),
        click_linkedin_addtoprofile: t('activity.eng.linkedin', '%who% added %badge% to LinkedIn'),
        click_pdf: t('activity.eng.pdf', '%who% downloaded the PDF of %badge%'),
      };
      const text = fill(v[e.event] || t('activity.eng.acted', '%who% interacted with %badge%'), vars);
      return { text: text + (e.event === 'share' ? net : '') };
    }
    if (e.kind === 'email') {
      const v: Record<string, string> = {
        send: t('activity.email.sent', 'Email sent to %who%'),
        delivery: t('activity.email.delivered', 'Email delivered to %who%'),
        open: t('activity.email.opened', '%who% opened the email'),
        bounce: t('activity.email.bounced', 'Email to %who% bounced'),
        complaint: t('activity.email.complaint', '%who% marked the email as spam'),
        reject: t('activity.email.rejected', 'Email to %who% was rejected'),
      };
      return { text: fill(v[e.event] || t('activity.email.event', 'Email event for %who%'), vars), sub: e.detail || null };
    }
    // portal
    const v: Record<string, string> = {
      page_view: t('activity.portal.pageView', '%actor% viewed %page%'),
      session_start: t('activity.portal.session', '%actor% started a session'),
      click: t('activity.portal.click', "%actor% clicked '%label%'"),
      badge_create_click: t('activity.portal.badgeStart', '%actor% started creating a badge'),
      badge_create_success: t('activity.portal.badgeCreated', '%actor% created a badge'),
      badge_create_error: t('activity.portal.badgeError', '%actor% hit an error creating a badge'),
      badge_create_blocked: t('activity.portal.badgeBlocked', '%actor% was blocked by a plan limit'),
    };
    const tmpl = v[e.event] || (e.event.startsWith('designer_')
      ? t('activity.portal.design', '%actor% edited a badge design — %what%')
      : t('activity.portal.generic', '%actor% — %what%'));
    return { text: fill(tmpl, vars), sub: e.event.startsWith('designer_') ? null : e.actor };
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.kind === filter);
  // "Include recipients" off → drop external-recipient events (engagement + email),
  // leaving only in-app team activity.
  const visible = includeRecipients ? filtered : filtered.filter(i => i.kind === 'portal');

  // Split a set of rows into workflow buckets (used both at top level and as the
  // sub-grouping inside each user).
  type SubGroup = { id: string; label: string; count: number | null; rows: ActivityItem[] };
  const workflowGroups = (rows: ActivityItem[]): SubGroup[] => {
    const buckets: Record<WorkflowKey, ActivityItem[]> = {
      badges: [], issuing: [], engagement: [], email: [], team: [], developers: [], browsing: [],
    };
    for (const it of rows) buckets[categorize(it)].push(it);
    return WORKFLOW_ORDER
      .filter(k => buckets[k].length > 0)
      .map(k => ({ id: k, label: `${WORKFLOW_META[k].icon} ${t(`activity.workflow.${k}`, WORKFLOW_META[k].en)}`, count: buckets[k].length, rows: buckets[k] }));
  };
  const dayGroups = (rows: ActivityItem[]): SubGroup[] => {
    const out: SubGroup[] = [];
    for (const it of rows) {
      const day = new Date(it.ts).toISOString().slice(0, 10);
      let g = out[out.length - 1];
      if (!g || g.id !== day) { g = { id: day, label: formatDate(new Date(it.ts), 'long'), count: null, rows: [] }; out.push(g); }
      g.rows.push(it);
    }
    return out;
  };

  // Top-level groups when NOT grouping by user.
  const displayGroups = groupBy === 'workflow' ? workflowGroups(visible) : dayGroups(visible);

  // Per-user groups (when grouping by user), ordered by most-recent activity.
  const userGroups = (() => {
    const map = new Map<string, { key: string; name: string; role: Role; rows: ActivityItem[]; last: number }>();
    for (const it of visible) {
      const p = personOf(it, myEmail);
      let g = map.get(p.key);
      if (!g) { g = { key: p.key, name: p.name, role: p.role, rows: [], last: 0 }; map.set(p.key, g); }
      g.rows.push(it);
      if (it.ts > g.last) g.last = it.ts;
    }
    return [...map.values()].sort((a, b) => b.last - a.last);
  })();

  const filters: Filter[] = ['all', 'engagement', 'email', 'portal'];
  const filterLabel: Record<Filter, string> = {
    all: t('activity.filter.all', 'All'),
    engagement: t('activity.filter.public', 'Public'),
    email: t('activity.filter.email', 'Email'),
    portal: t('activity.filter.portal', 'My team'),
  };

  const renderRows = (rows: ActivityItem[]) => (
    <ul className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {rows.map((e, i) => {
        const d = describe(e);
        const st = KIND_STYLE[e.kind];
        return (
          <li key={i} className="flex items-start gap-3 border-b border-gray-50 px-4 py-3 last:border-0">
            <span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${st.dot}`} />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-800">{d.text}</p>
              {d.sub && <p className="truncate text-xs text-gray-400">{d.sub}</p>}
            </div>
            <span className="flex-none whitespace-nowrap text-xs text-gray-400" title={new Date(e.ts).toLocaleString(i18n.language)}>{relTime(e.ts, now)}</span>
          </li>
        );
      })}
    </ul>
  );

  // Within a section: honor the Time|Workflow toggle. Time = flat; Workflow = sub-headers.
  const renderSection = (rows: ActivityItem[]) => {
    if (groupBy !== 'workflow') return renderRows(rows);
    return (
      <div className="space-y-3">
        {workflowGroups(rows).map(sg => (
          <div key={sg.id}>
            <h3 className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-gray-500"><span>{sg.label}</span><span className="text-gray-400">· {sg.count}</span></h3>
            {renderRows(sg.rows)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="page-wide px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('activity.title', 'Activity')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('activity.subtitle', 'Everything happening across your account — how people engage with your credentials, email delivery, and what your team does in the app.')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Group by user + include-recipients checkboxes */}
          <label className="inline-flex cursor-pointer select-none items-center gap-1.5 text-sm text-gray-600">
            <input type="checkbox" checked={groupByUser} onChange={e => setGroupByUser(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            {t('activity.groupByUser', 'Group by user')}
          </label>
          <label className={`inline-flex select-none items-center gap-1.5 text-sm ${groupByUser ? 'cursor-pointer text-gray-600' : 'cursor-pointer text-gray-500'}`}>
            <input type="checkbox" checked={includeRecipients} onChange={e => setIncludeRecipients(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            {t('activity.includeRecipients', 'Include recipients')}
          </label>
          {/* Group-by toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
            {(['time', 'workflow'] as GroupBy[]).map(g => (
              <button key={g} onClick={() => setGroupBy(g)}
                className={`rounded-md px-3 py-1 font-medium transition-colors ${groupBy === g ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {g === 'time' ? t('activity.groupBy.time', 'Time') : t('activity.groupBy.workflow', 'Workflow')}
              </button>
            ))}
          </div>
          {/* Kind filter */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
            {filters.map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`rounded-md px-3 py-1 font-medium transition-colors ${filter === f ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {filterLabel[f]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-gray-400">{t('common.loading', 'Loading…')}</div>
      ) : error ? (
        <div className="mt-10 text-sm text-red-500">{t('activity.error', 'Could not load activity.')}</div>
      ) : visible.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
          {t('activity.empty', 'No activity yet. Issue and share a credential — engagement and delivery events will show up here.')}
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {groupByUser ? (
            userGroups.map(u => {
              const role = ROLE_META[u.role];
              return (
                <div key={u.key}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600">{initialOf(u.name)}</span>
                    <span className="truncate text-sm font-semibold text-gray-800">{u.name}</span>
                    <span className={`flex-none rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${role.tag}`}>{t(`activity.role.${u.role}`, role.en)}</span>
                    <span className="flex-none rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">{u.rows.length}</span>
                    <span className="ml-auto flex-none whitespace-nowrap text-xs text-gray-400">{relTime(u.last, now)}</span>
                  </div>
                  {renderSection(u.rows)}
                </div>
              );
            })
          ) : (
            displayGroups.map(g => (
              <div key={g.id}>
                <h2 className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  <span>{g.label}</span>
                  {g.count != null && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-gray-500">{g.count}</span>}
                </h2>
                {renderRows(g.rows)}
              </div>
            ))
          )}
          {before && filter === 'all' && (
            <div className="text-center">
              <button onClick={loadMore} disabled={loadingMore}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50">
                {loadingMore ? t('common.loading', 'Loading…') : t('activity.loadMore', 'Load older activity')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
